import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import {
  HarshDrivingReportsHookParams,
  useFilteredDriverHarshEventsSummary,
} from "../hooks/useHarshDrivingReportsHooks";
import type { DriverHarshEventsTotalDto } from "../../../common/data/service-proxies";
import "../fuel-economy-report/FuelEconomyReport.css";

const formatCount = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return String(value);
};

export interface HarshDrivingTableProps extends HarshDrivingReportsHookParams {}

export const HarshDrivingTable: React.FC<HarshDrivingTableProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const {
    data,
    loading: isLoading,
    error,
  } = useFilteredDriverHarshEventsSummary({
    fromDate,
    toDate,
    periodType,
    skipCount: page * rowsPerPage,
    maxResultCount: rowsPerPage,
  });
  const isError = !!error;
  const rows: DriverHarshEventsTotalDto[] = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const onPageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);
  const onRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
    []
  );

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("harshDrivingReport.harshDrivingTable", {
            _: "Harsh driving by driver",
          })}
        </div>
      </div>
      <CardContent style={{ padding: 16 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <TableContainer style={{ overflow: "auto" }}>
            <Table size="small" style={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translate("common.truck", { _: "Truck" })}
                  </TableCell>
                  <TableCell>
                    {translate("common.driver", { _: "Driver" })}
                  </TableCell>
                  <TableCell>
                    {translate("harshDrivingReport.harshBrakes", {
                      _: "Harsh brakes",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("harshDrivingReport.harshStops", {
                      _: "Harsh stops",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("harshDrivingReport.harshTurns", {
                      _: "Harsh turns",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("harshDrivingReport.harshAccelerations", {
                      _: "Harsh accelerations",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("harshDrivingReport.speedingOccurrences", {
                      _: "Speeding occurrences",
                    })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: DriverHarshEventsTotalDto, index: number) => (
                  <TableRow
                    key={`${row.vehicleName ?? ""}-${row.operator ?? ""}-${
                      row.id ?? index
                    }`}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.vehicleName ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.operator ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatCount(row.harshBrakes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatCount(row.harshStops)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatCount(row.harshTurns)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatCount(row.harshAccelerations)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatCount(row.speedingOccurrences)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={translate("common.rowsPerPage", {
                _: "Rows per page",
              })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${translate("common.of", { _: "of" })} ${
                  count !== -1 ? count : `more than ${to}`
                }`
              }
            />
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default HarshDrivingTable;
